<template>
  <div class="blog-index">
    <header class="blog-hero">
      <div class="lp-container blog-hero__inner">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('blog.badge') }}</div>
        <h1 class="lp-h1 blog-hero__title lp-balance">{{ $t('blog.title') }}</h1>
        <p class="lp-lead blog-hero__lead lp-balance">{{ $t('blog.subtitle') }}</p>
      </div>
    </header>

    <section class="lp-container blog-list">
      <content-tabs :tabs="tabs" :label="$t('blog.title')" />

      <!-- Las guías son el otro apartado del blog: el que busca quien ya tiene
           la app abierta y no sabe dónde pulsar. Se entra por categoría para no
           volcar aquí las 36. -->
      <div class="guides-block">
        <div class="guides-block__head">
          <div>
            <h2 class="guides-block__title">{{ guidesUi.title }}</h2>
            <p class="guides-block__lead">{{ guidesUi.fromBlogLead }}</p>
          </div>
          <router-link :to="langPath('/guides')" class="guides-block__all">
            {{ guidesUi.allGuides }} <l-icon size="15">mdi-arrow-right</l-icon>
          </router-link>
        </div>

        <div class="cat-grid">
          <router-link
            v-for="cat in guideCategories"
            :key="cat.key"
            :to="langPath(`/guides#cat-${cat.key}`)"
            class="cat-card"
          >
            <span class="cat-card__icon"><l-icon size="18" color="primary">{{ cat.icon }}</l-icon></span>
            <span class="cat-card__name">{{ cat.name }}</span>
            <span class="cat-card__count">{{ cat.count }} {{ guidesUi.guides }}</span>
          </router-link>
        </div>
      </div>

      <h2 class="section-title">{{ $t('nav.blog') }}</h2>
      <div class="post-grid">
        <router-link
          v-for="post in posts"
          :key="post.slug"
          :to="`/blog/${post.slug}`"
          class="post-card"
        >
          <div class="post-top">
            <span class="post-icon"><l-icon size="18" color="primary">{{ post.icon }}</l-icon></span>
            <span class="post-tag">{{ post.tag }}</span>
          </div>
          <h2 class="post-title">{{ post.title }}</h2>
          <p class="post-desc">{{ post.description }}</p>
          <div class="post-foot">
            <span class="post-read">{{ $t('blog.readMore') }} <l-icon size="15">mdi-arrow-right</l-icon></span>
            <span class="post-time">{{ post.readingTime }} {{ $t('blog.minRead') }}</span>
          </div>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import ContentTabs from '@/components/ContentTabs.vue';
import { POSTS, localized } from './posts';
import { CATEGORIES, GUIDES, GUIDE_UI, guideLang, localizedCategory } from '../guides/guides';

export default {
  name: 'BlogIndex',
  components: { ContentTabs },
  metaInfo() {
    return {
      title: this.$t('blog.title'),
      titleTemplate: '%s | Nukli',
      meta: [
        { name: 'description', content: this.$t('blog.subtitle') },
        { property: 'og:title', content: `${this.$t('blog.title')} | Nukli` },
        { property: 'og:description', content: this.$t('blog.subtitle') },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://nukliapp.com/blog' },
      ],
    };
  },
  computed: {
    posts() {
      const locale = this.$i18n.locale;
      return POSTS.map((p) => localized(p, locale));
    },
    // Las guías solo existen en ca/es/en: sus rótulos hablan su mismo idioma.
    guidesUi() {
      return GUIDE_UI[guideLang(this.$i18n.locale)];
    },
    guideCategories() {
      const lang = guideLang(this.$i18n.locale);
      return CATEGORIES.map((c) => ({
        ...localizedCategory(c, lang),
        count: GUIDES.filter((g) => g.category === c.key).length,
      }));
    },
    tabs() {
      return [
        { label: this.$t('nav.blog'), to: this.langPath('/blog'), active: true },
        { label: this.$t('nav.guides'), to: this.langPath('/guides'), active: false, count: GUIDES.length },
      ];
    },
  },
  methods: {
    // El router conserva el prefijo de idioma al navegar, pero el href que se
    // pinta no: sin esto, abrir la pestaña en una ventana nueva desde /ca/blog
    // aterrizaba en la versión inglesa.
    langPath(path) {
      const l = this.$route.params.lang;
      return l ? `/${l}${path}` : path;
    },
  },
};
</script>

<style scoped>
.blog-index { background: var(--lp-bg); min-height: calc(100vh - 64px); }

.blog-hero {
  position: relative;
  /* 64px del AppBar fijo + aire, como las páginas legales */
  padding: 128px 0 8px;
  text-align: center;
}
.blog-hero__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}
.blog-hero__title { font-size: clamp(2.4rem, 5vw, 3.6rem); }
.blog-hero__lead { max-width: 560px; }

.blog-list { padding-bottom: 112px; padding-top: 56px; }

.section-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.45rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--lp-ink);
  margin: 0 0 20px;
}

.guides-block { margin-bottom: 56px; }
.guides-block__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;
}
.guides-block__title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.45rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--lp-ink);
  margin: 0 0 4px;
}
.guides-block__lead { margin: 0; color: var(--lp-muted); font-size: 0.98rem; }
.guides-block__all {
  flex: none;
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.92rem; font-weight: 600; color: var(--lp-accent);
  text-decoration: none;
}
.guides-block__all:hover { text-decoration: underline; }

.cat-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; }
.cat-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 18px;
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  background: var(--lp-surface);
  text-decoration: none;
  box-shadow: var(--lp-shadow-sm);
  transition: border-color 0.2s ease, transform 0.2s ease;
}
.cat-card:hover { border-color: var(--lp-accent); transform: translateY(-3px); }
.cat-card__icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-accent-weak);
}
.cat-card__name {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600; font-size: 0.98rem; line-height: 1.3;
  color: var(--lp-ink);
}
.cat-card__count { font-size: 0.84rem; color: var(--lp-muted); }

@media (max-width: 959px) { .cat-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 599px) {
  .cat-grid { grid-template-columns: repeat(2, 1fr); }
  .guides-block__head { flex-direction: column; align-items: flex-start; gap: 10px; }
}
.post-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}
.post-card {
  display: flex;
  flex-direction: column;
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius-lg);
  padding: 28px;
  text-decoration: none;
  box-shadow: var(--lp-shadow-sm);
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}
.post-card:hover {
  transform: translateY(-4px);
  border-color: var(--lp-line-strong);
  box-shadow: var(--lp-shadow-md);
}
.post-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.post-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
}
.post-tag {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--lp-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.post-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: var(--lp-ink);
  margin: 0 0 12px;
}
.post-desc {
  font-size: 0.975rem;
  line-height: 1.6;
  color: var(--lp-muted);
  margin: 0 0 24px;
  flex: 1;
}
.post-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 18px;
  border-top: 1px solid var(--lp-line);
}
.post-read {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.925rem;
  font-weight: 600;
  color: var(--lp-accent);
}
.post-time { font-size: 0.825rem; color: var(--lp-muted); }

@media (max-width: 759px) {
  .post-grid { grid-template-columns: 1fr; }
  .blog-list { padding-bottom: 72px; }
}
</style>
